import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { formatMoney } from '@/platform/lib/utils';
import {
    Container,
    FilterBarDropdown,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { Link, usePage } from '@inertiajs/react';
import qs from 'qs';
import { useMemo } from 'react';

const tabs = [
    {
        id: 'revenue',
        name: 'Facturatie omzet',
    },
    {
        id: 'payments',
        name: 'Facturatie uitkeringen',
    },
];

const OverviewView = () => {
    const { invoices, resellers, statuses, years, months } = usePage().props;
    const { filters, updateFilters } = useFilters(['invoices']);

    const totals = useMemo(() => {
        const totals = {
            subtotal: 0,
            vat: 0,
            total: 0,
            payed: 0,
            count: 0,
            count_payed: 0,
            payed_percentage: 0,
            count_payed_percentage: 0,
        };

        invoices?.map(period => {
            totals['subtotal'] += +period?.subtotal || 0;
            totals['vat'] += +period?.vat || 0;
            totals['total'] += +period?.total || 0;
            totals['payed'] += +period?.payed || 0;
            totals['count'] += +period?.count || 0;
            totals['count_payed'] += +period?.count_payed || 0;
            totals['payed_percentage'] += +period?.payed_percentage || 0;
            totals['count_payed_percentage'] +=
                +period?.count_payed_percentage || 0;
        });

        return totals;
    }, [invoices]);

    return (
        <Container fluid>
            <Segment>
                <SegmentHeader header={`Facturen ${filters.year}`} />
                <SegmentContent>
                    <SearchBarFilter only={['invoices']}>
                        <FilterBarDropdown
                            label={`Resellers (${filters?.resellers?.length || 0}/${Object.keys(resellers || {})?.length || 0})`}
                            options={Object.entries(resellers || {})?.map(
                                ([id, name]) => ({ id, name })
                            )}
                            multiple
                            clearable
                            value={filters?.resellers || []}
                            onChange={values =>
                                updateFilters('resellers', values)
                            }
                        />
                        <FilterBarDropdown
                            label={
                                statuses?.find(s => s.id == filters?.status)
                                    ?.name || 'Alle statusen'
                            }
                            options={statuses || []}
                            clearable
                            value={filters?.status || ''}
                            onChange={values => updateFilters('status', values)}
                        />
                        <FilterBarDropdown
                            label={filters?.year || 'Jaar'}
                            options={years?.map(year => ({
                                id: year,
                                name: year,
                            }))}
                            value={filters?.year || ''}
                            onChange={values => updateFilters('year', values)}
                        />
                        <FilterBarDropdown
                            label={
                                tabs?.find(tab => tab.id == filters?.tab)
                                    ?.name || 'Tab'
                            }
                            options={tabs || []}
                            value={filters?.tab || ''}
                            onChange={values => updateFilters('tab', values)}
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Periode</TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Bedrag
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    BTW
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Totaal
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Betaald
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Verschil
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Aantal
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Aantal betaald
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Object.keys(months || {})?.map(monthNumber => {
                                const monthData =
                                    invoices?.find(
                                        invoice => invoice.month == monthNumber
                                    ) || {};

                                return (
                                    <TableRow key={monthNumber}>
                                        <TableCell className='capitalize'>
                                            <Link
                                                href={`/invoices?${qs.stringify(
                                                    {
                                                        month: monthNumber,
                                                        year: filters.year,
                                                        resellers:
                                                            filters?.resellers ||
                                                            [],
                                                        status: filters?.status,
                                                        tab: filters.tab,
                                                    }
                                                )}`}
                                            >
                                                {months[monthNumber]}
                                            </Link>
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {formatMoney(
                                                monthData?.subtotal || 0
                                            )}
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {formatMoney(monthData?.vat || 0)}
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {formatMoney(monthData?.total || 0)}
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {formatMoney(monthData?.payed || 0)}
                                            <small className='text-gray-400'>
                                                (
                                                {`${monthData?.payed_percentage || 0}%`}
                                                )
                                            </small>
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {formatMoney(
                                                (monthData?.total || 0) -
                                                    (monthData?.payed || 0)
                                            )}
                                        </TableCell>
                                        <TableCell textAlign='center'>
                                            {monthData?.count || 0}
                                        </TableCell>
                                        <TableCell textAlign='right'>
                                            {monthData?.count_payed || 0}
                                            <small className='text-gray-400'>
                                                (
                                                {monthData?.count_payed_percentage ||
                                                    0}
                                                %)
                                            </small>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell>Totaal</TableCell>
                                <TableCell textAlign='right'>
                                    {formatMoney(totals?.subtotal || 0)}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {formatMoney(totals?.vat || 0)}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {formatMoney(totals?.total || 0)}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {`${formatMoney(totals?.payed || 0)} `}
                                    <small className='text-gray-400'>
                                        (
                                        {(
                                            totals?.payed_percentage / 12
                                        ).toFixed(2) || 0}
                                        %)
                                    </small>
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {formatMoney(
                                        (totals?.total || 0) -
                                            (totals?.payed || 0)
                                    )}
                                </TableCell>
                                <TableCell textAlign='center'>
                                    {totals?.count || 0}
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {totals?.count_payed || 0}
                                    <small className='text-gray-400'>
                                        (
                                        {(
                                            totals?.count_payed_percentage / 12
                                        ).toFixed(2) || 0}
                                        %)
                                    </small>
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </SegmentContent>
            </Segment>
        </Container>
    );
};

export default OverviewView;
